import React from 'react';
import { StyleSheet } from 'react-native';
import { DNABox, DNAButton, DNAText } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors';

export type SearchRating = 'positive' | 'negative';

type SearchRatingButtonState = {
  appearance: 'filled' | 'outline';
  status: 'success' | 'warning' | 'tertiary';
};

interface AISearchFeedbackProps {
  selectedRating: SearchRating | null;
  onRate: (rating: SearchRating) => void;
}

const styles = StyleSheet.create({
  selectedButton: {
    borderWidth: 0,
    borderColor: 'transparent',
  },
  selectedButtonText: {
    color: colors['color-text-white'],
  },
  selectedButtonIcon: {
    color: colors['color-text-white'],
  },
});

export const buildSearchRatingEventPayload = (
  questionText: string,
  flowId: string,
  rating: SearchRating,
) => ({
  category: 'SEARCH',
  action: 'RATING',
  rating,
  questionText,
  flowId,
});

export const getSearchRatingButtonState = (
  rating: SearchRating,
  selectedRating: SearchRating | null,
): SearchRatingButtonState => {
  const isSelected = rating === selectedRating;

  if (rating === 'positive') {
    return {
      appearance: isSelected ? 'filled' : 'outline',
      status: isSelected ? 'success' : 'tertiary',
    };
  }

  return {
    appearance: isSelected ? 'filled' : 'outline',
    status: isSelected ? 'warning' : 'tertiary',
  };
};

const AISearchFeedback: React.FC<AISearchFeedbackProps> = ({
  selectedRating,
  onRate,
}) => {
  const positiveButtonState = getSearchRatingButtonState('positive', selectedRating);
  const negativeButtonState = getSearchRatingButtonState('negative', selectedRating);
  const isPositiveSelected = selectedRating === 'positive';
  const isNegativeSelected = selectedRating === 'negative';

  return (
    <DNABox appearance="col" spacing="sm" testID="search-rating-feedback">
      <DNAText h5>Was this summary helpful?</DNAText>
      <DNABox appearance="row" spacing="sm" wrap="start">
        <DNAButton
          testID="search-rating-positive-btn"
          iconLeft="thumbs-up"
          appearance={positiveButtonState.appearance}
          status={positiveButtonState.status}
          style={isPositiveSelected ? styles.selectedButton : undefined}
          iconStyle={isPositiveSelected ? styles.selectedButtonIcon : undefined}
          accessibilityState={{ selected: selectedRating === 'positive' }}
          onPress={() => onRate('positive')}
        >
          <DNAText
            b1
            status={isPositiveSelected ? undefined : 'primary'}
            style={isPositiveSelected ? styles.selectedButtonText : undefined}
          >
            This was helpful
          </DNAText>
        </DNAButton>
        <DNAButton
          testID="search-rating-negative-btn"
          iconLeft="thumbs-down"
          appearance={negativeButtonState.appearance}
          status={negativeButtonState.status}
          style={isNegativeSelected ? styles.selectedButton : undefined}
          iconStyle={isNegativeSelected ? styles.selectedButtonIcon : undefined}
          accessibilityState={{ selected: selectedRating === 'negative' }}
          onPress={() => onRate('negative')}
        >
          <DNAText
            b1
            status={isNegativeSelected ? undefined : 'primary'}
            style={isNegativeSelected ? styles.selectedButtonText : undefined}
          >
            This was not helpful
          </DNAText>
        </DNAButton>
      </DNABox>
    </DNABox>
  );
};

export default AISearchFeedback;
